/**
 * S4.2 — der **Betreiber-Vorlagenkatalog**: kuratierte Playbook-Vorlagen, die ein Mandant
 * übernimmt und anpasst. Bauplan: `docs/KONZEPT-GEDAECHTNIS.md` § 2b, Abschnitt „Ausbaustufe:
 * Betreiber-Vorlagen" — *„Kuratierter Katalog globaler Vorlagen (`origin='betreiber_vorlage'`),
 * die Mandanten übernehmen und anpassen. Vorlagen stehen in der Hierarchie ÜBER
 * Mandanten-Playbooks."*
 *
 * ── Warum der Katalog im Code steht und nicht in einer globalen Tabelle ──────────────────────
 * „Betreiber" ist hier NICHT der Gastro-Betrieb (das wäre der Mandant), sondern der Betreiber der
 * Software. Ein kuratierter Katalog von ihm ist redaktioneller Inhalt mit derselben Tragweite wie
 * ein Systemtext: er wirkt in JEDEM Mandanten, der ihn übernimmt, und steht in der Rangfolge über
 * dem, was der Mandant selbst geschrieben hat. Genau deshalb liegt er hier und nicht in einer
 * Tabelle mit einer Pflege-Oberfläche:
 *
 *  - **Er wird wie Code geprüft.** Jede Änderung geht durch einen PR, durch das Lint (Ratsche
 *    `apps/api/test/playbook-vorlagen.test.ts` fährt `lintPlaybook` über JEDEN Eintrag) und durch
 *    die CI. Eine globale Tabelle hätte einen Schreibweg ohne diese Prüfung — und Fehler dort
 *    wirken sofort in allen Mandanten.
 *  - **Es gibt keinen Löschpfad-Zuwachs.** `playbooks` ist mandantengeschlüsselt und hängt an
 *    `tenants` (ON DELETE CASCADE, sql/166). Übernehmen legt eine ganz normale Mandantenzeile an.
 *    Eine zweite, mandantenlose Ablage wäre ein eigener DSGVO-/Löschpfad-Fall — genau das, wovor
 *    KONZEPT § 5 warnt („ein Datenhaushalt, ein Löschpfad").
 *  - **Übernehmen ist eine Kopie, kein Verweis.** Der Mandant darf die übernommene Fassung frei
 *    ändern; sie driftet dann bewusst von der Vorlage weg. Ein Verweis würde bedeuten, dass eine
 *    spätere Katalog-Änderung des Betreibers ungefragt das Verhalten im Betrieb eines Kunden
 *    ändert. Das wäre das Gegenteil der Transparenz-Zusage aus § 2b.
 *
 * ── Was eine Vorlage NICHT ist ───────────────────────────────────────────────────────────────
 * Kein Ersatz für ein fehlendes Werkzeug (§ 2b „Abgrenzung zu Werkzeug-Lücken") und keine
 * Erweiterung der Autonomie: ein Playbook kann Autonomie nur EINSCHRÄNKEN, nie erweitern
 * (Systemprompt-Rangfolge, `apps/agent/src/maestro-agent.ts`). Vorlagen enthalten deshalb
 * bewusst keine Beträge, keine Freigabe-Abkürzungen und keine Kontaktdaten — das Lint erzwingt
 * das, der Text hält sich ohnehin daran.
 *
 * ── Eigenes Paket, kein `apps/api`-Modul ─────────────────────────────────────────────────────
 * Der Katalog wird an zwei Stellen gebraucht: die Übernahme-Route in `apps/api` und die Anzeige
 * im Playbook-Reiter in `apps/web`. Zwei Kopien wären der Anfang des Auseinanderdriftens —
 * dasselbe Argument wie bei `@maestro/transparenz-hinweis`. Bewusst KEIN `test`-Skript: geprüft
 * wird der Katalog von `apps/api/test/playbook-vorlagen.test.ts`, weil die Prüfung das Lint aus
 * `apps/api` braucht und `packages/*` nicht auf `apps/*` zugreifen darf (Ratsche
 * `apps/api/test/ci-testabdeckung.test.ts` kennt diese Konstellation).
 */

/**
 * Die Vorgangstypen, auf die eine Vorlage gescopt sein darf. Deckungsgleich mit `ZIEL_BEREICH`
 * in `apps/api/src/routes/gedaechtnis.ts` — nur diese Werte matcht `loadPlaybooks` gegen
 * `scope->>'vorgangstyp'`. Ein Tippfehler hier hieße: die Vorlage greift nie. Die Ratsche hält
 * die Liste gegen die Route.
 */
export const VORLAGE_VORGANGSTYPEN = ["veranstaltung", "angebot", "kunde", "anfrage", "speisekarten_import"] as const;
export type VorlageVorgangstyp = (typeof VORLAGE_VORGANGSTYPEN)[number];

export interface PlaybookVorlage {
  /**
   * Stabiler Schlüssel, über den die Übernahme-Route die Vorlage findet. Ändert sich NIE — er
   * steht in den `evidence_refs` der übernommenen Zeile und ist damit die einzige Verbindung
   * zwischen Mandantenzeile und Katalogeintrag.
   */
  schluessel: string;
  /** Der Name, unter dem die Vorlage beim Übernehmen im Mandanten landet. */
  name: string;
  /** Ein Satz für die Katalog-Kachel: wofür sie gut ist. Kein Marketing. */
  wofuer: string;
  /**
   * `null` = greift in jedem Vorgang (leerer Scope). Sonst der Vorgangstyp, auf den die Vorlage
   * gescopt ist.
   */
  vorgangstyp: VorlageVorgangstyp | null;
  /** Der Anweisungstext selbst, ≤2.000 Zeichen (CHECK in sql/166). */
  bodyMd: string;
}

/**
 * Der Katalog. Bewusst klein: fünf Vorlagen, die in JEDEM Catering-/Gastro-Betrieb gelten und
 * keine Annahme über Küche, Preisniveau oder Kundschaft treffen. Eine Vorlage, die nur für
 * manche Betriebe stimmt, gehört nicht in einen Katalog, der über dem steht, was der Mandant
 * selbst geschrieben hat — sie wäre dort eine stille Fehlanweisung.
 */
export const PLAYBOOK_VORLAGEN: readonly PlaybookVorlage[] = Object.freeze([
  {
    schluessel: "rueckfragen-buendeln",
    name: "Rückfragen bündeln",
    wofuer: "Offene Punkte in einer Nachricht klären statt in fünf einzelnen.",
    vorgangstyp: "anfrage",
    bodyMd:
      "Wenn zu einer Anfrage mehrere Angaben fehlen, sammle sie und stelle sie in EINER Nachricht " +
      "zusammen — nummeriert, höchstens fünf Punkte. Frage nicht nach etwas, das in der Anfrage " +
      "schon steht oder das aus einem früheren Vorgang desselben Kunden bekannt ist. " +
      "Fehlt nur eine einzige Angabe, frage nur danach.",
  },
  {
    schluessel: "unvertraeglichkeiten-erfassen",
    name: "Unverträglichkeiten früh erfassen",
    wofuer: "Allergien und Ernährungsformen gehören vor das Angebot, nicht in die Woche davor.",
    vorgangstyp: "anfrage",
    bodyMd:
      "Kläre Allergien, Unverträglichkeiten und Ernährungsformen (vegetarisch, vegan, halal, " +
      "koscher), BEVOR ein Angebot zusammengestellt wird — nicht erst bei der Bestätigung. " +
      "Halte fest, wie viele Gäste betroffen sind, nicht wer. Namen einzelner Gäste werden nicht " +
      "notiert. Ist die Angabe unklar (Beispiel: ein paar Vegetarier), frage nach einer Zahl.",
  },
  {
    schluessel: "eckdaten-vor-dem-angebot",
    name: "Eckdaten vor dem Angebot prüfen",
    wofuer: "Gästezahl, Uhrzeit, Ort und Ablauf stehen fest, bevor gerechnet wird.",
    vorgangstyp: "angebot",
    bodyMd:
      "Bevor du ein Angebot zusammenstellst, prüfe, ob Gästezahl, Datum, Beginn und Ende, Ort " +
      "und die Art der Bewirtung (Buffet, Menü, Flying) bekannt sind. Fehlt eine dieser fünf " +
      "Angaben, weise darauf hin und schlage vor, sie zu klären — stelle das Angebot trotzdem " +
      "zusammen, wenn der Betreiber es so will, aber sage dazu, worauf du geraten hast und was " +
      "sich dadurch noch ändern kann.",
  },
  {
    schluessel: "zusagen-nur-nach-ruecksprache",
    name: "Zusagen nur nach Rücksprache",
    wofuer: "Der Assistent formuliert Entwürfe, verbindlich wird der Betrieb.",
    vorgangstyp: null,
    bodyMd:
      "Formuliere gegenüber Kunden nichts als verbindlich zugesagt, was der Betrieb nicht " +
      "ausdrücklich bestätigt hat — weder Termine noch Verfügbarkeiten noch Leistungen. " +
      "Schreibe Entwürfe so, dass sie als Vorschlag lesbar bleiben, und lege dem Betreiber vor, " +
      "was er bestätigen soll. Im Zweifel lieber eine Rückfrage zu viel als eine Zusage zu früh.",
  },
  {
    schluessel: "aenderungen-nachvollziehbar",
    name: "Änderungen nachvollziehbar machen",
    wofuer: "Wer später hineinschaut, sieht, was sich geändert hat und warum.",
    vorgangstyp: "veranstaltung",
    bodyMd:
      "Wenn sich an einer Veranstaltung etwas Wesentliches ändert (Gästezahl, Datum, Ort, " +
      "Leistungsumfang), halte in einem Satz fest, WAS sich geändert hat und auf wessen Wunsch. " +
      "Fasse mehrere kleine Änderungen desselben Gesprächs zu einer Notiz zusammen, statt jede " +
      "einzeln zu vermerken.",
  },
]);

/** Findet eine Vorlage über ihren Schlüssel. `undefined`, wenn es sie nicht (mehr) gibt. */
export function findeVorlage(schluessel: string): PlaybookVorlage | undefined {
  return PLAYBOOK_VORLAGEN.find((v) => v.schluessel === schluessel);
}

/**
 * Der `scope`, mit dem eine übernommene Vorlage in `playbooks` landet — dieselbe Form, die
 * `loadPlaybooks` (apps/api/src/lib/gedaechtnis-kontext.ts) matcht: leeres Objekt = greift
 * immer, sonst `{ vorgangstyp }`.
 */
export function vorlageScope(vorlage: PlaybookVorlage): Record<string, unknown> {
  return vorlage.vorgangstyp === null ? {} : { vorgangstyp: vorlage.vorgangstyp };
}

/**
 * Die Priorität, mit der eine Vorlage angelegt wird. Bewusst DERSELBE Default wie bei einem
 * Mandanten-Playbook (100, sql/166): der Vorrang der Vorlage kommt NICHT aus einer höheren Zahl,
 * sondern aus der Sortierung nach `origin` in `loadPlaybooks`. Eine Zahl wäre umkippbar, sobald
 * ein Bediener seinem eigenen Playbook 200 gibt — die Rangfolge aus § 2b ist aber keine
 * Verhandlungssache.
 */
export const VORLAGE_PRIORITAET = 100;
